/** Quotes a CSV field only when it needs it (contains a comma, quote, or newline). */
function quoteCsvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/** Serializes rows (header + example rows) into RFC 4180 CSV text. */
export function toCsv(rows: string[][]): string {
  return rows.map((row) => row.map(quoteCsvField).join(",")).join("\r\n")
}

/** Triggers a browser download of `content` as a file named `filename`. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * Sniffs whether the file uses "," or ";" as the field delimiter, by counting
 * each candidate outside quoted spans on the header line (the first line
 * with any content). Excel in several locales (e.g. es-CO, most of Europe)
 * exports CSV with ";" because "," is the decimal separator there — admins
 * pasting/uploading such a file otherwise get every column silently
 * concatenated into one, which fails validation in confusing ways or,
 * worse, imports garbage into the wrong column.
 */
function detectDelimiter(text: string): "," | ";" {
  const headerLine = text.split(/\r\n|\n|\r/).find((line) => line.trim() !== "") ?? ""
  let commaCount = 0
  let semicolonCount = 0
  let inQuotes = false
  for (const char of headerLine) {
    if (char === '"') inQuotes = !inQuotes
    else if (!inQuotes && char === ",") commaCount++
    else if (!inQuotes && char === ";") semicolonCount++
  }
  return semicolonCount > commaCount ? ";" : ","
}

/**
 * Minimal RFC 4180 CSV parser (quoted fields, embedded commas/newlines, "" escapes).
 * Handles the small "paste from Excel/Sheets" files the admin import pages need —
 * not a general-purpose CSV library. Auto-detects "," vs ";" as the delimiter
 * (see detectDelimiter) so both comma- and semicolon-exported files work.
 */
export function parseCsv(text: string): string[][] {
  const delimiter = detectDelimiter(text)
  const rows: string[][] = []
  let row: string[] = []
  let field = ""
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const next = text[i + 1]

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"'
        i++
      } else if (char === '"') {
        inQuotes = false
      } else {
        field += char
      }
      continue
    }

    if (char === '"') {
      inQuotes = true
    } else if (char === delimiter) {
      row.push(field)
      field = ""
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && next === "\n") i++
      row.push(field)
      field = ""
      if (row.some((cell) => cell !== "")) rows.push(row)
      row = []
    } else {
      field += char
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field)
    if (row.some((cell) => cell !== "")) rows.push(row)
  }

  return rows
}
